import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, type WebUser } from '../api/users';
import { subscriptionApi } from '../api/subscription';
import { useAuth } from '../context/AuthContext';
import { confirm } from '../components/ConfirmDialog';

export default function UserManagementPage() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const [modal, setModal] = useState<null | 'create' | WebUser>(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'staff' as 'owner' | 'staff', newPassword: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: users = [], isLoading } = useQuery({ queryKey: ['users'], queryFn: usersApi.list, staleTime: 30 * 1000 });
  const { data: sub } = useQuery({ queryKey: ['subscription'], queryFn: subscriptionApi.get, staleTime: 60_000 });
  const usersMax = sub?.limits.usersMax ?? Infinity;
  const atUserLimit = sub ? users.length >= usersMax : false;

  const del = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); qc.refetchQueries({ type: 'all' }); },
  });

  function openCreate() {
    setForm({ email: '', password: '', name: '', role: 'staff', newPassword: '' });
    setError(null); setModal('create');
  }
  function openEdit(u: WebUser) {
    setForm({ email: u.email, password: '', name: u.name ?? '', role: u.role as 'owner' | 'staff', newPassword: '' });
    setError(null); setModal(u);
  }

  async function handleSave() {
    if (modal === 'create') {
      if (!form.email.trim()) { setError('Email is required'); return; }
      // Must match LoginDto's backend minimum (8) — a shorter password used
      // to be accepted here and create an account that could never log in.
      if (!form.password || form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    } else if (form.newPassword && form.newPassword.length < 8) {
      setError('Password must be at least 8 characters'); return;
    }
    setError(null); setSaving(true);
    try {
      if (modal === 'create') {
        await usersApi.create({ email: form.email, password: form.password, name: form.name || undefined, role: form.role });
      } else {
        const userId = (modal as WebUser).id;
        await usersApi.update(userId, { name: form.name || undefined, role: form.role });
        if (form.newPassword) {
          await usersApi.resetPassword(userId, form.newPassword);
        }
      }
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.refetchQueries({ type: 'all' });
      setModal(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  if (currentUser?.role !== 'owner') {
    return (
      <div className="page">
        <div className="page-header"><h1 className="page-title">User Management</h1></div>
        <div className="error-box">Only owners can manage users.</div>
      </div>
    );
  }

  return (
    <div className="page" style={{ maxWidth: 800 }}>
      <div className="page-header">
        <h1 className="page-title">User Management</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {sub && <span style={{ fontSize: '0.82rem', color: atUserLimit ? 'var(--danger)' : 'var(--text-muted)' }}>{users.length} / {usersMax === Infinity ? '∞' : usersMax} users</span>}
          <button className="btn btn-primary" onClick={openCreate} disabled={atUserLimit} title={atUserLimit ? `Your plan allows ${usersMax} user${usersMax === 1 ? '' : 's'}. Upgrade to add more.` : undefined}>+ Invite User</button>
        </div>
      </div>
      {atUserLimit && (
        <div className="warn-box">
          You've reached the user limit for your plan ({usersMax} user{usersMax === 1 ? '' : 's'}).{' '}
          <a href="/subscription" style={{ fontWeight: 700, textDecoration: 'underline' }}>Upgrade your plan</a> to invite more users.
        </div>
      )}

      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : users.length === 0 ? <div className="empty-state"><div className="empty-state-icon">👤</div><div className="empty-state-text">No users yet</div></div>
          : (
            <table>
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Since</th><th style={{ width: 120 }}>Actions</th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ opacity: 1 }}>
                    <td style={{ fontWeight: 600 }}>
                      {u.name ?? '—'}
                      {u.id === currentUser?.id && <span className="badge badge-blue" style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>You</span>}
                    </td>
                    <td style={{ color: 'var(--text-2)' }}>{u.email}</td>
                    <td>
                      <span className={`badge ${u.role === 'owner' ? 'badge-paid' : 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{u.role}</span>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{u.createdAt.slice(0, 10)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}>Edit</button>
                        {u.id !== currentUser?.id && (
                          <button className="btn btn-danger btn-sm" disabled={del.isPending} onClick={async () => {
                            if (await confirm({ message: `Remove ${u.name ?? u.email} from your account?`, danger: true })) del.mutate(u.id);
                          }}>Remove</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Invite User' : 'Edit User'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box">{error}</div>}
              <div className="form-row form-row-2">
                <label>Name<input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" autoFocus /></label>
                <label>Role
                  <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as 'owner' | 'staff' }))}>
                    <option value="staff">Staff</option>
                    <option value="owner">Owner</option>
                  </select>
                </label>
              </div>
              {modal === 'create' && (
                <>
                  <div className="form-row">
                    <label>Email *<input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="user@company.com" /></label>
                  </div>
                  <div className="form-row">
                    <label>Password *<input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 8 characters" /></label>
                  </div>
                </>
              )}
              {modal !== 'create' && (
                <>
                  <div style={{ background: 'var(--surface-alt)', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    Email: <strong>{(modal as WebUser).email}</strong>
                  </div>
                  <div className="form-row" style={{ marginTop: '0.75rem' }}>
                    <label>New Password <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank to keep current)</span>
                      <input type="password" value={form.newPassword} onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="Min 8 characters" />
                    </label>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : modal === 'create' ? 'Create User' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
